import { useRef } from './utils/object'

const createDummyModule = (name) => {
  return new Proxy(
    {
      name,
      _calls: [],
      _inner: null,
      isDummy: true,
      swapFor(module) {
        this._calls.forEach(([method, args]) => {
          const fn = module[method]

          if (!fn) {
            // we should throw error, but that might fail subsequent calls
            console.error(`Unknown method '${method}' on module '${name}'`)
            return
          }

          fn.apply(module, args)
        })

        this._inner = module
      },
    },
    {
      get(target, prop) {
        if (target._inner) {
          // real module is loaded
          return target._inner[prop]
        }

        if (prop in target) {
          return target[prop]
        }

        return new Proxy(() => {}, {
          apply(_, __, argArr) {
            target._calls.push([prop, argArr])
          },
        })
      },
    }
  )
}

const locator = (() => {
  const refMap = {}

  const createRef = (key, defValue) => {
    const val = defValue || createDummyModule(key)
    const [get, set] = useRef(val)

    const ref = {
      get,
      set(val) {
        if (!val) {
          throw Error('Module must be defined')
        }

        const dummy = get()

        if (!dummy.isDummy) {
          throw Error('Module should only be set once')
        }

        dummy.swapFor(val)

        set(val)
      },
    }
    refMap[key] = ref

    return ref
  }

  const ensureRef = (key, defValue) => {
    if (key in refMap) {
      if (defValue) {
        console.warn(
          `Key ${key} seem to be registered more than once, given default value may not be used`
        )
      }
      return refMap[key]
    }

    return createRef(key, defValue)
  }

  const getModule = (key) => {
    const ref = ensureRef(key)
    return ref.get()
  }

  const setModule = (key, value) => {
    const ref = ensureRef(key)
    ref.set(value)
  }

  return new Proxy(
    {
      ensureRef,
      getModule,
      setModule,
    },
    {
      set(_, prop, val) {
        setModule(prop, val)
        return true
      },
      get(target, prop) {
        if (prop in target) {
          return target[prop]
        }
        return getModule(prop)
      },
    }
  )
})()

export default locator
